import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { networkManager } from '../services/NetworkManager';
import { DefinitionError, Definition } from '../types';
import { useNotification } from '../contexts/NotificationContext';

const AddDefinitionPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<DefinitionError[]>([]);
  const [errorType, setErrorType] = useState<'syntax' | 'semantic' | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [useTextEditor, setUseTextEditor] = useState(false);
  const [definitionText, setDefinitionText] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // States for based on dropdown
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [selectedBasedOn, setSelectedBasedOn] = useState<Definition | null>(null);
  const [showBasedOnDropdown, setShowBasedOnDropdown] = useState(false);
  const [basedOnSearchTerm, setBasedOnSearchTerm] = useState('');
  const basedOnDropdownRef = useRef<HTMLDivElement>(null);

  // Load definitions for based on dropdown
  useEffect(() => {
    const loadDefinitions = async () => {
      try {
        const definitionsData = await networkManager.getDefinitions();
        setDefinitions(definitionsData || []);
      } catch (err) {
        console.error('Error loading definitions:', err);
        setDefinitions([]);
      }
    };
    
    loadDefinitions();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (basedOnDropdownRef.current && !basedOnDropdownRef.current.contains(event.target as Node)) {
        setShowBasedOnDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateAndSetFile = (file: File) => {
    // Validate file extension
    const allowedExtensions = ['.txt', '.bsk'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(fileExtension)) {
      showNotification('Please select a valid file type (.txt or .bsk)', 'error', 5000);
      setSelectedFile(null);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return false;
    }
    
    setSelectedFile(file);
    setValidationErrors([]); // Clear validation errors
    setErrorType(null);
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      if (validateAndSetFile(file)) {
        // Update the file input value to match the dropped file
        if (fileInputRef.current) {
          // Create a new FileList-like object
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          fileInputRef.current.files = dataTransfer.files;
        }
      }
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDefinitionText(e.target.value);
    // Don't clear validation errors automatically - let them persist until next submission
  };



  const handleSubmit = async (e: React.FormEvent) => {
    console.log('=== SUBMIT EVENT TRIGGERED ===');
    e.preventDefault();
    console.log('handleSubmit called');
    console.log('useTextEditor:', useTextEditor);
    console.log('selectedFile:', selectedFile);
    console.log('definitionText:', definitionText);
    console.log('formData:', formData);
    
    if (!useTextEditor && !selectedFile) {
      console.log('No file selected');
      showNotification('Please select a definition file', 'error', 5000);
      return;
    }

    if (useTextEditor && !definitionText.trim()) {
      console.log('No text content');
      showNotification('Please enter definition content', 'error', 5000);
      return;
    }

    try {
      console.log('Starting to create definition...');
      setLoading(true);
      setValidationErrors([]);
      setErrorType(null);

      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      
      if (useTextEditor) {
        // Create a file from the text content
        const file = new File([definitionText], `${formData.title}.bsk`, { type: 'text/plain' });
        formDataToSend.append('definition', file);
      } else {
        formDataToSend.append('definition', selectedFile!);
      }
      
      // Add basedOn if selected
      if (selectedBasedOn) {
        formDataToSend.append('basedOn', selectedBasedOn._id);
      }

      const newDefinition = await networkManager.createDefinition(formDataToSend);
      
      // Show success notification
      showNotification(`Definition "${newDefinition.title}" created successfully!`, 'success', 5000);
      
      // Success - redirect to the definition detail page
      if (newDefinition && newDefinition._id) {
        navigate(`/definitions/${newDefinition._id}`);
      } else {
        // Fallback to definitions list if no ID
        navigate('/definitions');
      }
    } catch (err: any) {
      console.error('Error creating definition:', err);
      console.log('Error object:', err);
      console.log('Error message:', err.message);
      console.log('Error type:', typeof err);
      console.log('Error constructor:', err.constructor.name);
      
      // Check if it's a validation error response with the expected format
      if (err.message && typeof err.message === 'string') {
        try {
          const errorData = JSON.parse(err.message);
          console.log('Parsed error data:', errorData);
          console.log('Error data type:', typeof errorData);
          console.log('Error data keys:', Object.keys(errorData));
          console.log('Has message property:', 'message' in errorData);
          console.log('Message value:', errorData.message);
          
          // Check if it matches the expected error format with array of errors
          if (errorData.errors && Array.isArray(errorData.errors) && 
              errorData.message && typeof errorData.message === 'string') {
            console.log('Setting validation errors (array):', errorData.errors);
            
            // Check if it's syntax errors or semantic errors
            if (errorData.message === 'Syntax errors detected') {
              // Transform syntax errors to format the message
              const formattedErrors = errorData.errors.map((err: any) => ({
                ErrorType: 2, // Error type for syntax errors
                Line: err.Line || 0,
                Column: 0,
                SpreadsheetName: 'definition',
                Message: `${err.Message} on line ${err.Line}. Unexpected token ${err.LastToken}`,
                Level: 2
              }));
              
              console.log('Formatted syntax errors:', formattedErrors);
              setValidationErrors(formattedErrors);
              setErrorType('syntax');
              
              // Show notification about syntax errors
              showNotification(`Syntax errors detected in definition file. Please check the details below.`, 'error', 8000);
            } else {
              // Semantic errors - keep current behavior
              setValidationErrors(errorData.errors);
              setErrorType('semantic');
              
              // Show notification about validation errors
              showNotification(`Definition validation failed with ${errorData.errors.length} error(s). Please check the details below.`, 'error', 8000);
            }
            
            // Scroll to errors section after a short delay to ensure DOM is updated
            setTimeout(() => {
              const errorsSection = document.getElementById('validation-errors-section');
              if (errorsSection) {
                errorsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
            
            return;
          }
          
          // Check if it's a syntax error with detailed error information
          if (errorData.errors && typeof errorData.errors === 'string' && 
              errorData.message && typeof errorData.message === 'string') {
            console.log('Setting syntax error (string):', errorData.errors);
            console.log('Syntax error type:', typeof errorData.errors);
            
            // Create a validation error object for syntax errors
            const syntaxError = {
              ErrorType: 2, // Error type for syntax errors
              Line: 0,
              Column: 0,
              SpreadsheetName: 'definition',
              Message: errorData.errors,
              Level: 2 // Error level
            };
            
            console.log('Created syntax error object:', syntaxError);
            setValidationErrors([syntaxError]);
            setErrorType('syntax');
            
            // Show notification about syntax errors
            showNotification(`Syntax errors detected in definition file. Please check the details below.`, 'error', 8000);
            
            // Scroll to errors section after a short delay to ensure DOM is updated
            setTimeout(() => {
              const errorsSection = document.getElementById('validation-errors-section');
              if (errorsSection) {
                errorsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
            
            return;
          }
          
          // If it's a general error with message
          if (errorData.message && typeof errorData.message === 'string') {
            console.log('Setting general error message:', errorData.message);
            console.log('Full error data:', errorData);
            const errorMessage = errorData.message;
            showNotification(errorMessage, 'error', 8000);
            return;
          }
          
          console.log('No matching error pattern found');
        } catch (parseError) {
          console.error('Error parsing validation errors:', parseError);
          console.error('Raw error message:', err.message);
        }
      }
      
      // If we couldn't parse as JSON, use the raw error message
      const errorMessage = err.message || 'Failed to create definition. Please try again.';
      console.log('Using raw error message:', errorMessage);
      showNotification(errorMessage, 'error', 8000);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/definitions');
  };

  // Based on dropdown functions
  const filteredDefinitions = (definitions || []).filter(def =>
    def.title.toLowerCase().includes(basedOnSearchTerm.toLowerCase())
  );

  const handleBasedOnSelect = (definition: Definition) => {
    setSelectedBasedOn(definition);
    setBasedOnSearchTerm(definition.title);
    setShowBasedOnDropdown(false);
  };

  const handleBasedOnInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBasedOnSearchTerm(value);
    setShowBasedOnDropdown(true);
    
    // Clear selection if input is cleared
    if (!value) {
      setSelectedBasedOn(null);
    }
  };

  const handleBasedOnInputFocus = () => {
    setShowBasedOnDropdown(true);
  };

  const clearBasedOnSelection = () => {
    setSelectedBasedOn(null);
    setBasedOnSearchTerm('');
    setShowBasedOnDropdown(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Add New Definition</h1>
          <p className="text-gray-600 mt-1">Upload a new definition file</p>
        </div>
        <button
          onClick={handleBack}
          className="btn btn-secondary"
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Definitions
        </button>
      </div>

      {/* Form Section - Always visible */}
      <div className="card p-8 mb-8">
        <div className="flex items-center mb-6">
          <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Definition Details</h2>
            <p className="text-sm text-gray-600">Fill in the information below</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Definition Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="input text-base"
                placeholder="Enter a descriptive title for your definition"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="input resize-none text-base"
                placeholder="Provide a brief description of what this definition contains (optional)"
              />
            </div>
          </div>

          <div>
            <label htmlFor="basedOn" className="block text-sm font-medium text-gray-700 mb-2">
              Based On (Optional)
            </label>
            <div className="relative" ref={basedOnDropdownRef}>
              <input
                type="text"
                id="basedOn"
                value={basedOnSearchTerm}
                onChange={handleBasedOnInputChange}
                onFocus={handleBasedOnInputFocus}
                className="input text-base w-full"
                placeholder="Search for a definition to base this on..."
                autoComplete="off"
              />
              
              {selectedBasedOn && (
                <button
                  type="button"
                  onClick={clearBasedOnSelection}
                  className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              
              {showBasedOnDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredDefinitions.length > 0 ? (
                    filteredDefinitions.map((definition) => (
                      <div
                        key={definition._id}
                        onClick={() => handleBasedOnSelect(definition)}
                        className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{definition.title}</div>
                        {definition.description && (
                          <div className="text-sm text-gray-600 mt-1 truncate">
                            {definition.description}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      No definitions found
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Select an existing definition to base this new definition on. This will inherit some properties from the selected definition.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="definition" className="block text-sm font-medium text-gray-700">
                Definition File *
              </label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">File Upload</span>
                <button
                  type="button"
                  onClick={() => setUseTextEditor(!useTextEditor)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    useTextEditor ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      useTextEditor ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-600">Text Editor</span>
              </div>
            </div>
            <div className="relative overflow-visible" style={{ minHeight: '300px' }}>
              {/* File Upload Section */}
              <div 
                className={`absolute inset-0 flex justify-center px-6 pt-8 pb-8 border-2 border-dashed rounded-xl transition-all duration-500 ease-in-out transform ${
                  useTextEditor 
                    ? 'opacity-0 -translate-x-full pointer-events-none' 
                    : isDragOver 
                      ? 'border-green-400 bg-green-50 border-solid opacity-100 translate-x-0' 
                      : 'border-gray-300 bg-gray-50 hover:border-green-400 hover:bg-gray-100 opacity-100 translate-x-0'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="space-y-3 text-center">
                  <svg className={`mx-auto h-16 w-16 transition-colors duration-200 ${
                    isDragOver ? 'text-green-500' : 'text-gray-400'
                  }`} stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex flex-col items-center text-sm text-gray-600">
                    <label htmlFor="definition" className="relative cursor-pointer bg-white rounded-lg font-medium text-green-600 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500 px-4 py-2 border border-green-300 hover:border-green-400 transition-colors">
                      <span>Choose a file</span>
                      <input
                        ref={fileInputRef}
                        id="definition"
                        name="definition"
                        type="file"
                        accept=".txt,.bsk"
                        onChange={handleFileChange}
                        className="sr-only"
                        required
                      />
                    </label>
                    <p className={`mt-2 transition-colors duration-200 ${
                      isDragOver ? 'text-green-600 font-medium' : 'text-gray-500'
                    }`}>
                      {isDragOver ? 'Drop your file here' : 'or drag and drop here'}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500">Supports TXT and BSK files up to 10MB</p>
                </div>
              </div>

              {/* Text Editor Section */}
              <div className={`transition-all duration-500 ease-in-out transform ${
                useTextEditor 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-full pointer-events-none absolute inset-0'
              }`}>
                <div className="flex flex-col">
                  <div className="mb-2">
                    <span className="text-sm font-medium text-gray-700">Definition Content</span>
                  </div>
                  <div className="relative">
                    <textarea
                      ref={textareaRef}
                      value={definitionText}
                      onChange={handleTextChange}
                      className="w-full resize-y text-base font-mono text-sm border-2 border-gray-300 rounded-xl p-4 focus:border-blue-500 focus:outline-none"
                      style={{ 
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        minHeight: '300px'
                      }}
                      placeholder="// Write your definition here...&#10;// Use // for single line comments&#10;// Example:&#10;&#10;message Player {&#10;  string name = 1;&#10;  int32 level = 2;&#10;}"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Write your definition content here. The text will be converted to a file when you submit.
                  </p>
                </div>
              </div>
            </div>

            {/* File info display - outside the container */}
            {selectedFile && !useTextEditor && (
              <div className="mt-3 flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-green-800">{selectedFile.name}</p>
                    <p className="text-xs text-green-600">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="text-red-600 hover:text-red-800 transition-colors"
                  title="Remove file"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}


          </div>

          <div className="pt-4">
            <button
              type="button"
              disabled={loading}
              className="btn btn-primary w-full py-3 text-base font-medium"
              onClick={() => {
                console.log('Button clicked!');
                console.log('Loading state:', loading);
                console.log('Button disabled:', loading);
                handleSubmit(new Event('submit') as any);
              }}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="spinner h-5 w-5 mr-2"></div>
                  Creating Definition...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Definition
                </div>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Validation Errors Section - Full width when present */}
      {validationErrors.length > 0 && (
        <div id="validation-errors-section" className="card p-8 border-2 border-red-300 bg-red-50">
          <div className="flex items-center mb-6">
                            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
              <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-red-800">
                Validation Errors ({validationErrors.length})
              </h2>
              <p className="text-sm text-red-600 font-medium">
                Please fix these {errorType === 'syntax' ? 'syntax' : errorType === 'semantic' ? 'semantic' : ''} errors and try again
              </p>
            </div>
          </div>

          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
            {validationErrors.map((err, index) => (
              <div key={index} className="bg-red-50 border border-red-200 rounded-xl p-4 hover:bg-red-100 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                                         <div className="flex items-center space-x-2 mb-3">
                       {err.Line > 0 && (
                         <span className="text-xs bg-red-200 text-red-800 px-3 py-1 rounded-full font-medium">
                           Line {err.Line}
                         </span>
                       )}
                       {err.SpreadsheetName && (
                         <span className="text-xs bg-blue-200 text-blue-800 px-3 py-1 rounded-full font-medium">
                           Structure: {err.SpreadsheetName}
                         </span>
                       )}
                     </div>
                    <div className="bg-red-100 border border-red-300 rounded-lg p-3">
                      <p className="text-sm text-red-800 whitespace-pre-line font-mono leading-relaxed">
                        {err.Message}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-5 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl">
            <div className="flex items-start">
              <div className="h-8 w-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-3 mt-0.5">
                <svg className="h-4 w-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-yellow-800 mb-2">
                  How to fix these errors:
                </p>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    Check the line and column numbers in your definition file
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    Verify that all referenced entities exist
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    Ensure proper syntax and formatting
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    Fix the errors and upload the corrected file
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDefinitionPage;
